/**
 * AI Card Inspection — server functions.
 *
 * `runAuctionInspection` is seller/staff only and performs the analysis.
 * `getAuctionInspection` is public and returns the stored advisory report for
 * a listing that buyers can see.
 */

import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { InspectionImage } from "./analyze.server";
import {
  INSPECTION_DISCLAIMER,
  isDefectKind,
  type CenteringEstimate,
  type InspectionFinding,
  type InspectionReport,
  type InspectionStatus,
  type Severity,
} from "./types";

const BUCKET_360 = "auction-360";
const SIGN_TTL = 60 * 30;
const MAX_IMAGES = 10;

function assertUuid(v: unknown, label: string): string {
  if (typeof v !== "string" || !/^[0-9a-f-]{36}$/i.test(v)) throw new Error(`Invalid ${label}`);
  return v;
}

type FindingRow = {
  id: string;
  kind: string;
  severity: string;
  confidence: number | string | null;
  description: string;
  set_type: string | null;
  frame_number: number | null;
  image_index: number | null;
  x: number | string | null;
  y: number | string | null;
  w: number | string | null;
  h: number | string | null;
  zoom: number | string | null;
};

function n(value: number | string | null, fallback: number): number {
  const v = typeof value === "string" ? Number(value) : value;
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}

function rowToFinding(r: FindingRow): InspectionFinding | null {
  if (!isDefectKind(r.kind)) return null;
  return {
    id: r.id,
    kind: r.kind,
    severity: (["minor", "moderate", "severe"] as const).includes(r.severity as Severity)
      ? (r.severity as Severity)
      : "minor",
    confidence: r.confidence === null ? null : n(r.confidence, 0),
    description: r.description,
    setType: r.set_type,
    frameNumber: r.frame_number,
    imageIndex: r.image_index,
    x: n(r.x, 0),
    y: n(r.y, 0),
    w: n(r.w, 0.08),
    h: n(r.h, 0.08),
    zoom: n(r.zoom, 4),
  };
}

const EMPTY: InspectionReport = {
  status: "pending",
  model: null,
  error: null,
  centering: null,
  findings: [],
  completedAt: null,
  disclaimer: INSPECTION_DISCLAIMER,
};

/** Public read — only for listings a buyer can already see. */
export const getAuctionInspection = createServerFn({ method: "GET" })
  .inputValidator((input: { auctionId: string }) => ({
    auctionId: assertUuid(input?.auctionId, "auction"),
  }))
  .handler(async ({ data }): Promise<InspectionReport | null> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: auction } = await supabaseAdmin
      .from("auctions")
      .select("id, status")
      .eq("id", data.auctionId)
      .maybeSingle();
    const visible = ["active", "ended", "awaiting_payment", "sold"];
    if (!auction || !visible.includes(String(auction.status))) return null;

    const { data: inspection } = await supabaseAdmin
      .from("auction_inspections")
      .select("status, model, error, centering, completed_at")
      .eq("auction_id", data.auctionId)
      .maybeSingle();
    if (!inspection) return null;

    const { data: rows } = await supabaseAdmin
      .from("auction_inspection_findings")
      .select("id, kind, severity, confidence, description, set_type, frame_number, image_index, x, y, w, h, zoom")
      .eq("auction_id", data.auctionId);

    return {
      status: inspection.status as InspectionStatus,
      model: inspection.model ?? null,
      error: inspection.error ?? null,
      centering: (inspection.centering as CenteringEstimate | null) ?? null,
      findings: ((rows ?? []) as FindingRow[])
        .map(rowToFinding)
        .filter((f): f is InspectionFinding => !!f),
      completedAt: inspection.completed_at ?? null,
      disclaimer: INSPECTION_DISCLAIMER,
    };
  });

/** Seller/staff read — works for drafts too, so the seller can review before publishing. */
export const getMyAuctionInspection = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { auctionId: string }) => ({
    auctionId: assertUuid(input?.auctionId, "auction"),
  }))
  .handler(async ({ data, context }): Promise<InspectionReport | null> => {
    const { supabase } = context;
    const { data: inspection } = await supabase
      .from("auction_inspections")
      .select("status, model, error, centering, completed_at")
      .eq("auction_id", data.auctionId)
      .maybeSingle();
    if (!inspection) return null;

    const { data: rows } = await supabase
      .from("auction_inspection_findings")
      .select("id, kind, severity, confidence, description, set_type, frame_number, image_index, x, y, w, h, zoom")
      .eq("auction_id", data.auctionId);

    return {
      ...EMPTY,
      status: inspection.status as InspectionStatus,
      model: inspection.model ?? null,
      error: inspection.error ?? null,
      centering: (inspection.centering as CenteringEstimate | null) ?? null,
      completedAt: inspection.completed_at ?? null,
      findings: ((rows ?? []) as FindingRow[])
        .map(rowToFinding)
        .filter((f): f is InspectionFinding => !!f),
    };
  });

/**
 * Run (or re-run) the advisory inspection for a listing the caller owns.
 * Uses the listing photos plus a sample of its 360° frames.
 */
export const runAuctionInspection = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { auctionId: string }) => ({
    auctionId: assertUuid(input?.auctionId, "auction"),
  }))
  .handler(async ({ data, context }): Promise<InspectionReport> => {
    const { supabase, userId } = context;

    const { enforceRateLimit } = await import("@/lib/rate-limit.server");
    await enforceRateLimit("ai-inspection", { userId, preset: "aiUser" });

    const { data: auction, error: aErr } = await supabase
      .from("auctions")
      .select("id, seller_id, images")
      .eq("id", data.auctionId)
      .maybeSingle();
    if (aErr) throw aErr;
    if (!auction) throw new Error("Listing not found");
    if (auction.seller_id !== userId) throw new Error("You can only inspect your own listings");

    const apiKey = process.env["LOVABLE_API_KEY"];
    if (!apiKey) throw new Error("AI is not configured for this project");

    // Mark as running so the buyer-facing panel can show progress.
    await supabase
      .from("auction_inspections")
      .upsert(
        {
          auction_id: data.auctionId,
          seller_id: userId,
          status: "running",
          error: null,
        },
        { onConflict: "auction_id" },
      );

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { analyzeCardImages } = await import("./analyze.server");

    const images: InspectionImage[] = [];

    // Still photos first — usually the highest quality view of the card.
    const photos = (auction.images ?? []).filter((u): u is string => typeof u === "string");
    photos.slice(0, 6).forEach((url, idx) => {
      images.push({ ref: `photo_${idx}`, url, setType: null, frameNumber: null, imageIndex: idx });
    });

    // A sample of rotation frames, so findings can be pinned to the turntable.
    const { data: sets } = await supabaseAdmin
      .from("auction_360_sets")
      .select("id, set_type")
      .eq("auction_id", data.auctionId)
      .in("status", ["ready", "locked"]);

    if (sets?.length) {
      const { data: frames } = await supabaseAdmin
        .from("auction_360_frames")
        .select("set_id, frame_number, optimized_path")
        .in(
          "set_id",
          sets.map((s) => s.id),
        )
        .order("frame_number", { ascending: true });

      const budget = Math.max(0, MAX_IMAGES - images.length);
      const perSet = Math.max(1, Math.floor(budget / sets.length));
      const picked: Array<{ path: string; setType: string; frameNumber: number }> = [];

      for (const set of sets) {
        const setFrames = (frames ?? []).filter((f) => f.set_id === set.id && f.optimized_path);
        if (!setFrames.length) continue;
        // Evenly spaced samples around the rotation.
        const step = Math.max(1, Math.floor(setFrames.length / perSet));
        for (let i = 0; i < setFrames.length && picked.length < budget; i += step) {
          const f = setFrames[i]!;
          picked.push({
            path: f.optimized_path!,
            setType: String(set.set_type),
            frameNumber: f.frame_number,
          });
        }
      }

      if (picked.length) {
        const { data: signed } = await supabaseAdmin.storage
          .from(BUCKET_360)
          .createSignedUrls(
            picked.map((p) => p.path),
            SIGN_TTL,
          );
        const byPath = new Map((signed ?? []).map((s) => [s.path, s.signedUrl]));
        for (const p of picked) {
          const url = byPath.get(p.path);
          if (!url) continue;
          images.push({
            ref: `${p.setType}#${p.frameNumber}`,
            url,
            setType: p.setType,
            frameNumber: p.frameNumber,
            imageIndex: null,
          });
        }
      }
    }

    if (!images.length) {
      await supabase
        .from("auction_inspections")
        .update({ status: "failed", error: "Add photos before running an inspection." })
        .eq("auction_id", data.auctionId);
      throw new Error("Add photos before running an inspection.");
    }

    try {
      const result = await analyzeCardImages(apiKey, images);

      const { data: row, error: upErr } = await supabase
        .from("auction_inspections")
        .upsert(
          {
            auction_id: data.auctionId,
            seller_id: userId,
            status: "ready",
            model: result.model,
            error: null,
            centering: result.centering,
            summary: {
              findings: result.findings.length,
              limitations: result.limitations,
              latency_ms: result.latencyMs,
              images: images.length,
            },
            completed_at: new Date().toISOString(),
          },
          { onConflict: "auction_id" },
        )
        .select("id, completed_at")
        .single();
      if (upErr) throw upErr;

      await supabase.from("auction_inspection_findings").delete().eq("auction_id", data.auctionId);

      if (result.findings.length) {
        const { error: fErr } = await supabase.from("auction_inspection_findings").insert(
          result.findings.map((f) => ({
            inspection_id: row.id,
            auction_id: data.auctionId,
            kind: f.kind,
            severity: f.severity,
            confidence: f.confidence,
            description: f.description,
            set_type: f.setType,
            frame_number: f.frameNumber,
            image_index: f.imageIndex,
            x: f.x,
            y: f.y,
            w: f.w,
            h: f.h,
            zoom: f.zoom,
          })),
        );
        if (fErr) throw fErr;
      }

      return {
        status: "ready",
        model: result.model,
        error: null,
        centering: result.centering,
        findings: result.findings,
        completedAt: row.completed_at,
        disclaimer: INSPECTION_DISCLAIMER,
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : "Inspection failed";
      await supabase
        .from("auction_inspections")
        .update({ status: "failed", error: message.slice(0, 500) })
        .eq("auction_id", data.auctionId);
      throw new Error(message);
    }
  });
